'use client';
/**
 * Fenêtre de rédaction d'une action corrective, ouverte À L'ENDROIT où l'action
 * vient de naître — fiche NC, réclamation, registre EEQ.
 *
 * Avant, créer une action depuis une NC repliait simplement le mini-formulaire :
 * l'action partait au plan avec l'intitulé recopié de la NC, et il fallait la
 * retrouver dans un autre écran pour la formuler. Depuis une EEQ c'était pire —
 * un bouton unique créait l'action sans rien demander, donc sans échéance : elle
 * ne pouvait jamais être comptée en retard.
 *
 * La fenêtre se superpose à l'écran courant, qui reste en place derrière : on
 * remplit l'action et on revient à ce qu'on faisait. Elle n'est pas un passage
 * obligé — l'action existe déjà quand elle s'ouvre ; la fermer laisse une action
 * valide, seulement moins précise.
 */
import React, { useState } from 'react';
import { useGed } from '@/lib/store';
import { ACTION_TYPES, ACTION_ORIGINS } from '@/lib/meta';
import { VOILE, COUCHES, I, inputStyle, FieldError, RequiredHint, useBusy, Selecteur } from '@/components/ui';
import { useSortieFenetre, AlerteSortie } from '@/shared/fenetreModale';
import type { GedAction } from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
const lbl: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5 };

export function ActionRedaction({ documentId, onClose }: { documentId: string | null; onClose: () => void }) {
  const { actions } = useGed();
  const a = actions.find((x) => x.documentId === documentId) || null;
  /* ⚠️ Plus de `useEscape` ICI : Échap appellerait `onClose` directement et
     contournerait l'alerte de saisie non enregistrée. C'est `useSortieFenetre`,
     dans le formulaire, qui écoute désormais — par la même porte que la croix. */
  if (!a) return null;
  // `key` plutôt qu'un effet de resynchronisation : le formulaire se réinitialise
  // en changeant d'action, et JAMAIS au rafraîchissement du store — sans quoi
  // toute autre écriture concurrente écraserait une saisie en cours.
  return <Formulaire key={a.documentId} a={a} onClose={onClose} />;
}

function Formulaire({ a, onClose }: { a: GedAction; onClose: () => void }) {
  const { settings, smq, ouvrirPanneau } = useGed();
  const gedUsers = settings?.gedUsers || [];
  const { run, estOccupe } = useBusy();
  const [f, setF] = useState({
    titre: a.titre || '', actType: a.actType || 'corrective',
    responsable: a.responsable || '', echeance: a.echeance || '', description: a.description || '',
  });
  /* CE QUE LA FENÊTRE A LU, FIGÉ À L'OUVERTURE (14/10/2026) — même forme que
     `f`, envoyé sous `base` : le moteur n'écrit que les champs changés ici, et
     refuse (409) celui qu'un autre aurait changé pendant la saisie. */
  const [base] = useState(() => ({ ...f }));
  const [erreur, setErreur] = useState('');

  const origine = ACTION_ORIGINS.find((o) => o.key === a.origine)?.label || a.origine;

  const enregistrer = () => run('save', async () => {
    if (!f.titre.trim()) { setErreur('L’intitulé est obligatoire.'); return; }
    setErreur('');
    const ok = await smq(`ged/actions/${a.documentId}/plan`, { ...f, base }, 'Action complétée — événement tracé.');
    if (ok) onClose();
  });

  const boite = React.useRef<HTMLDivElement | null>(null);
  const sortie = useSortieFenetre({ boite, onClose });

  return (
    /* ⚠️ AUCUNE fermeture sur le voile — règle du 04/09/2026, voir
       shared/ui/fenetreModale.tsx. On sort par la croix, et si l'on a saisi
       quelque chose, l'alerte le dit d'abord. */
    <div {...sortie.proprietesVoile} role="presentation"
      style={{ position: 'fixed', inset: 0, zIndex: COUCHES.MODALE, background: VOILE, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div ref={boite} {...sortie.proprietesBoite} role="dialog" aria-modal="true" aria-labelledby="acred-titre"
        style={{ width: '100%', maxWidth: 640, maxHeight: '90vh', background: '#fff', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', overflow: 'auto', display: 'flex', flexDirection: 'column', position: 'relative', ...sortie.proprietesBoite.style }}>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '18px 24px 14px', borderBottom: '1px solid var(--border-default)' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--color-primary)', marginBottom: 5 }}>
              Action portée au plan · origine {origine}{a.origineRef ? ` ${a.origineRef}` : ''}
            </div>
            <h2 id="acred-titre" style={{ margin: 0, fontSize: 19, fontWeight: 700, color: 'var(--text-strong)' }}>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-primary-active)' }}>{a.ref}</span> — à compléter
            </h2>
            <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: 480 }}>
              L’action existe déjà : ce que vous précisez ici la rend suivable. Vous pouvez fermer et y revenir depuis le plan d’actions.
            </p>
          </div>
          <Bouton taille="sm" onClick={sortie.demanderSortie} aria-label="Fermer"
            style={{ flex: 'none', ...(sortie.refuse ? { borderColor: 'var(--color-primary)', color: 'var(--color-primary-active)' } : null) }}>✕</Bouton>
        </div>

        <div style={{ padding: '16px 24px', display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><RequiredHint /></div>

          <div>
            <label style={lbl} htmlFor="acred-t">Intitulé <span style={{ color: 'var(--red-600)' }}>*</span></label>
            <input id="acred-t" value={f.titre} onChange={(e) => { setF({ ...f, titre: e.target.value }); if (erreur) setErreur(''); }}
              aria-invalid={!!erreur} style={{ ...inputStyle, borderColor: erreur ? 'var(--red-600)' : undefined }} />
            <FieldError>{erreur}</FieldError>
          </div>

          <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
            <div>
              <label style={lbl} htmlFor="acred-type">Type</label>
              <select id="acred-type" value={f.actType} onChange={(e) => setF({ ...f, actType: e.target.value as GedAction['actType'] })} style={inputStyle}>
                {ACTION_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl} htmlFor="acred-resp">Responsable</label>
              <Selecteur id="acred-resp" value={f.responsable} onChange={(v) => setF({ ...f, responsable: v })} style={inputStyle}>
                <option value="">—</option>
                {gedUsers.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
              </Selecteur>
            </div>
          </div>

          <div>
            <label style={lbl} htmlFor="acred-ech">Échéance</label>
            <input id="acred-ech" type="date" value={f.echeance} onChange={(e) => setF({ ...f, echeance: e.target.value })} style={{ ...inputStyle, maxWidth: 220 }} />
            {!f.echeance && (
              <div style={{ marginTop: 6, fontSize: 12.5, color: 'var(--amber-700, #8A5B00)', background: 'var(--amber-50, #FFF8EC)', borderRadius: 'var(--radius-md)', padding: '8px 12px' }}>
                Sans échéance, cette action ne sera jamais signalée en retard.
              </div>
            )}
          </div>

          <div>
            <label style={lbl} htmlFor="acred-desc">Description</label>
            <textarea id="acred-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={4}
              style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', padding: '14px 24px 18px', borderTop: '1px solid var(--border-default)' }}>
          <Bouton variante="primaire" onClick={() => void enregistrer()} disabled={estOccupe()}>
            <I n="check" s={14} />{estOccupe('save') ? 'Enregistrement…' : 'Enregistrer l’action'}
          </Bouton>
          <Bouton onClick={onClose}>Fermer</Bouton>
          {/* Cette fenêtre est ouverte AU-DESSUS de la NC ou de la réclamation
              qu'on instruisait : envoyer au plan d'actions en pleine page
              éjecterait de cette fiche. La fiche action s'ouvre donc en
              panneau, par-dessus l'écran de départ resté en place. */}
          <Bouton taille="sm" onClick={() => { ouvrirPanneau({ type: 'action', valeur: a.documentId, origine: a.origineRef || origine }); onClose(); }} style={{ marginLeft: 'auto' }}>
            Ouvrir la fiche de l’action →
          </Bouton>
        </div>
        {sortie.alerte && <AlerteSortie onRester={sortie.rester} onQuitter={sortie.quitterSansEnregistrer} />}
      </div>
    </div>
  );
}
